import { observer, useLocalObservable } from 'mobx-react';
import { Fragment } from 'react';
import {
  FUND_SEARCH_CATEGORY_FIELDS,
  getErrorFields,
  SEARCH_FIELDS,
  trimStateFields
} from '../../../../utils/constants/fields';
import {
  formatNumber,
  getFilledSearchCategoriesCount,
  mapFieldsToState
} from '../../../../utils/utils';
import { paths } from '../../../../utils/constants/routes';
import { SEARCH_CATEGORIES } from '../../../../utils/constants/searchCategories';
import { UI_OPTIONS } from '../../../../utils/constants/uiOptions';
import { UI_OPTION_KEYS } from '../../../../utils/constants/uiOptionKeys';
import { useStore } from '../../../../store/store';
import useHistory from '../../../../hooks/useHistory';
import { getFundsInvestmentCriteriaSearchFields } from '../agent/constants/fundsInvestmentCriteriaSearchFields';
import FundsInvestmentCriteriaSearchFormDesktop from '../agent/FundsInvestmentCriteriaSearchFormDesktop';
import FundsInvestmentCriteriaSearchFormMobile from '../agent/FundsInvestmentCriteriaSearchFormMobile';
import SearchFormMobileSection from '../agent/SearchFormMobileSection';
import getErroredSections from '../../utils/getErroredSections';

/**
 * FundsSearch - Self-contained Funds search component
 *
 * Manages its own state for:
 * - Investment criteria fields with EBITDA calculations
 * - Mobile filters display
 * - Validation and search execution
 *
 * @param {React.ReactNode} tabs - Tab navigation component from parent
 * @param {boolean} isFromInvestorView - Whether rendered from investor view
 * @param {function} onMobileFilterChange - Callback when mobile filter state changes
 */
const FundsSearch = observer(({ tabs, isFromInvestorView = false, onMobileFilterChange }) => {
  const { profilerAccessStore } = useStore();
  const { navigate } = useHistory();

  const state = useLocalObservable(() => ({
    // Mobile filters state
    isMobileFiltersDisplayed: false,
    toggleMobileFilters: () => {
      state.isMobileFiltersDisplayed = !state.isMobileFiltersDisplayed;
      const savedScrollY = state.savedScrollY;
      state.savedScrollY = 0;
      setTimeout(() => window.scrollTo({ top: savedScrollY }), 0);
      onMobileFilterChange?.(state.isMobileFiltersDisplayed);
    },
    savedScrollY: 0,
    mobileFilterSection: null,
    setMobileFilterSection: (value) => {
      state.savedScrollY = window.scrollY;
      state.mobileFilterSection = value;
      state.isMobileFiltersDisplayed = true;
      onMobileFilterChange?.(true);
    },

    // Category state
    categoryFields: {
      [FUND_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY.NAME]: [
        UI_OPTIONS[UI_OPTION_KEYS.FUND_SEARCH_CATEGORY]['Investment criteria']
      ]
    },
    setCategoryFieldValue: (field = {}, value) => {
      state.categoryFields[field.NAME] = value;
    },
    get categories() {
      return Object.entries(UI_OPTIONS[UI_OPTION_KEYS.FUND_SEARCH_CATEGORY]).map(
        ([name, value]) => ({
          name,
          value,
          disabled: value !== UI_OPTIONS[UI_OPTION_KEYS.FUND_SEARCH_CATEGORY]['Investment criteria']
        })
      );
    },

    // Fields state
    fields: {
      ...mapFieldsToState(SEARCH_FIELDS),
      [SEARCH_FIELDS.SEARCH_TYPE.NAME]: [UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Funds]
    },
    setFieldValue: (field = {}, value) => {
      state.fields[field.NAME] = value;
    },
    get fieldsCount() {
      return getFilledSearchCategoriesCount(state.fields, SEARCH_CATEGORIES, SEARCH_FIELDS);
    },

    // Validation state
    onSubmitErrorState: false,
    setOnSubmitErrorState: (value = false) => (state.onSubmitErrorState = value),
    get validationFields() {
      return getErrorFields(Object.values(SEARCH_FIELDS), state.fields);
    },
    get isSearchDisabled() {
      return (
        !!state.validationFields.invalidFields.filter((f) => !f.isOnSubmit).length ||
        (state.onSubmitErrorState && !!state.validationFields.invalidFields.length)
      );
    },

    // UI form state for EBITDA calculations
    uiFormState: {
      isEBITDAFieldFocused: false,
      isEBITDAMarginFieldFocused: false,
      get hasRevenueValue() {
        return !!state.fields[SEARCH_FIELDS.REVENUE.NAME]?.length;
      },
      get isEBITDAFieldDisabled() {
        return !state.uiFormState.hasRevenueValue || state.uiFormState.isEBITDAMarginFieldFocused;
      },
      get isEBITDAMarginFieldDisabled() {
        return !state.uiFormState.hasRevenueValue || state.uiFormState.isEBITDAFieldFocused;
      },
      onRevenueChange: (revenue) => {
        const hasValue = !!revenue?.length;
        if (hasValue) {
          const EBITDAValue = state.fields[SEARCH_FIELDS.EBITDA.NAME];
          const EBITDAMarginValue = state.fields[SEARCH_FIELDS.EBITDA_MARGIN.NAME];
          if (EBITDAValue) {
            state.fields[SEARCH_FIELDS.EBITDA_MARGIN.NAME] = formatNumber(
              ((+EBITDAValue / +revenue) * 100).toFixed(2),
              ''
            );
          } else if (EBITDAMarginValue) {
            state.fields[SEARCH_FIELDS.EBITDA.NAME] = formatNumber(
              ((+revenue * +EBITDAMarginValue) / 100).toFixed(2),
              ''
            );
          }
        }
      },
      onEBITDAChange: (EBITDA) => {
        const hasValue = !!EBITDA?.length;
        if (hasValue) {
          const revenue = state.fields[SEARCH_FIELDS.REVENUE.NAME];
          state.fields[SEARCH_FIELDS.EBITDA_MARGIN.NAME] = formatNumber(
            ((+EBITDA / +revenue) * 100).toFixed(2),
            ''
          );
        }
      },
      onEBITDAFocus: () => {
        state.uiFormState.isEBITDAFieldFocused = true;
      },
      onEBITDABlur: () => {
        state.uiFormState.isEBITDAFieldFocused = false;
      },
      onEBITDAMarginChange: (EBITDAMargin) => {
        const hasValue = !!EBITDAMargin?.length;
        if (hasValue) {
          const revenue = state.fields[SEARCH_FIELDS.REVENUE.NAME];
          state.fields[SEARCH_FIELDS.EBITDA.NAME] = formatNumber(
            ((+revenue * +EBITDAMargin) / 100).toFixed(2),
            ''
          );
        }
      },
      onEBITDAMarginFocus: () => {
        state.uiFormState.isEBITDAMarginFieldFocused = true;
      },
      onEBITDAMarginBlur: () => {
        state.uiFormState.isEBITDAMarginFieldFocused = false;
      }
    },

    // Compatibility layer for field builders that expect old state shape
    get fundsInvestmentCriteriaFields() {
      return state.fields;
    },
    get setFundsInvestmentCriteriaFieldValue() {
      return state.setFieldValue;
    },
    get fundsInvestmentCriteriaValidationFields() {
      return state.validationFields;
    },
    get onFundsInvestmentCriteriaSubmitErrorState() {
      return state.onSubmitErrorState;
    },
    get disableProfilerSearchSelectionMobile() {
      return !profilerAccessStore.canUseProfilerSearch;
    }
  }));

  const executeSearch = () => {
    const selectedCategory = state.categoryFields[FUND_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY.NAME][0];

    if (selectedCategory !== UI_OPTIONS[UI_OPTION_KEYS.FUND_SEARCH_CATEGORY]['Investment criteria']) {
      return;
    }

    trimStateFields(state.fields);
    if (state.validationFields.invalidFields.length) {
      if (!state.onSubmitErrorState) {
        state.setOnSubmitErrorState(true);
      }
      return;
    }

    const fields = Object.entries(state.fields)
      .map(([k, v]) => `${[k]}=${encodeURIComponent(JSON.stringify(v))}`)
      .join('&');

    navigate(
      paths.SEARCH_RESULTS +
        `?${FUND_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY.NAME}=${encodeURIComponent(
          selectedCategory
        )}&page=1&${fields}`
    );
  };

  const onSearch = (e) => {
    e?.preventDefault?.();
    executeSearch();
  };

  // Build field components
  const fieldComponents = getFundsInvestmentCriteriaSearchFields(
    state,
    state.uiFormState,
    isFromInvestorView,
    false // disableSearchType - not applicable for Funds tab
  );

  // Compute errored sections
  const erroredSections = getErroredSections({
    categories: SEARCH_CATEGORIES,
    fieldsConfig: SEARCH_FIELDS,
    hasSubmitError: state.onSubmitErrorState,
    validationFields: state.validationFields
  });

  // Only show Investment criteria for now (other categories disabled)
  const selectedCategory = state.categoryFields[FUND_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY.NAME][0];
  if (selectedCategory !== UI_OPTIONS[UI_OPTION_KEYS.FUND_SEARCH_CATEGORY]['Investment criteria']) {
    return null;
  }

  // Mobile filter section view
  if (state.isMobileFiltersDisplayed) {
    return (
      <SearchFormMobileSection
        section={state.mobileFilterSection}
        onBack={state.toggleMobileFilters}
        fields={fieldComponents}
      />
    );
  }

  return (
    <Fragment>
      <FundsInvestmentCriteriaSearchFormDesktop
        fields={fieldComponents}
        isSearchDisabled={state.isSearchDisabled}
        onSearch={onSearch}
        uiFormState={state.uiFormState}
        tabs={tabs}
        categoryFields={state.categoryFields}
        setCategoryFieldValue={state.setCategoryFieldValue}
        categoryOptions={state.categories}
      />
      <FundsInvestmentCriteriaSearchFormMobile
        onShowSection={(section) => state.setMobileFilterSection(section)}
        fieldsCount={state.fieldsCount}
        isSearchDisabled={state.isSearchDisabled}
        onSearch={onSearch}
        fields={fieldComponents}
        erroredSections={erroredSections}
        uiFormState={state.uiFormState}
        categoryFields={state.categoryFields}
        setCategoryFieldValue={state.setCategoryFieldValue}
        categoryOptions={state.categories.filter(({ disabled }) => !disabled)}
      />
    </Fragment>
  );
});

export default FundsSearch;
